import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const buildStepLabels = (t) => [
  t('onboarding:steps.company', 'Company'),
  t('onboarding:steps.useCase', 'Use case'),
  t('onboarding:steps.background', 'Background'),
];

/**
 * WizardProgressBar — radix-inspired stepper with discrete pill segments
 * for each step. Visually matches the rest of the modernized app: slate
 * idle / primaryColor active / slim heights / no heavy fills.
 */
const WizardProgressBar = ({ currentStep, totalSteps, labels }) => {
  const { t } = useTranslation('onboarding');
  const stepLabels = labels || buildStepLabels(t).slice(0, totalSteps);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2 pr-10">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {t('common:floatingPanel.stepProgress', 'Step {{current}} of {{total}}', {
            current: currentStep,
            total: totalSteps,
          })}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {stepLabels[currentStep - 1]}
        </span>
      </div>
      <div
        className="flex items-center gap-1.5"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
      >
        {Array.from({ length: totalSteps }).map((_, i) => {
          const isActive = i < currentStep;
          return (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${
                isActive ? 'bg-primaryColor' : 'bg-slate-200'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

WizardProgressBar.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
};

export default WizardProgressBar;
